/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable sonarjs/cognitive-complexity */
import { Injectable } from '@angular/core';
import { HttpEventType, HttpEvent } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { RouterFacade } from '@cigna/core/router-state';
import { Observable, of, EMPTY, merge } from 'rxjs';
import {
  map,
  switchMap,
  mergeMap,
  catchError,
  withLatestFrom,
  concatMap,
  filter,
  tap,
} from 'rxjs/operators';
import { Store, Action } from '@ngrx/store';
import { WindowService } from '@cigna/core/window';
import {
  SearchClaim,
  ClaimsResultsResponse,
  RemittanceDetailsResponse,
  RemittanceDetailsRequest,
  PayeeInfo,
  RemittanceReadResponse,
  ClaimTab,
  ClaimsInfo,
  Adjustment,
  dtFormat,
  ClaimDetails,
  RemittanceSummary,
} from '@cigna/chcp/claims/shared/util';
import {
  UserFacade,
  FlagUpdateType,
} from '@cigna/chcp/shared/user-profile-data-access';

import { AnalyticsService } from '@cigna/analytics/data-access';
import {
  SearchClaimsError,
  SearchClaims,
  ClaimsSearchActionTypes,
  SearchClaimsSuccess,
  ClaimsNumberSearch,
  ClaimsNumberSearchSuccess,
  ClaimsNumberSearchError,
  DisplayClaimsDetails,
  LoadClaimsDetailsSuccess,
  LoadClaimsDDARSuccess,
  LoadClaimsDetailsError,
  MultiplePatients,
  MultiplePatientsSuccess,
  RemittanceReadStatus,
  RemittanceReadStatusSuccess,
  RemittanceReadStatusError,
  FetchAdjustmentsSuccess,
  FetchAdjustmentsError,
  SetHistoryDataIndex,
  GetProviderByTinSuccess,
  GetProviderByTinError,
  GetProviderByTin,
  RemoveAdjustmentSuccess,
  RemoveAdjustmentError,
  GetAttachmentsSignedUrlToPutFile,
  GetAttachmentsSignedUrlToPutFileSuccess,
  GetAttachmentsSignedUrlToPutFileFailed,
  GetSupportingInformationAttachments,
  GetSupportingInformationAttachmentsSuccess,
  GetSupportingInformationAttachmentsFailed,
  PutFileToS3Success,
  PutFileToS3Failed,
  FileToS3UploadProgress,
  GetAttachmentDetailsForUploadedSuccess,
  GetAttachmentDetailsForUploadedFailed,
  AttachmentClick,
  SubmitSupportingInformationSuccess,
  SubmitSupportingInformationFailed,
  GetSupportingInformationHistorySuccess,
  GetSupportingInformationHistoryFailed,
  RemoveAttachment,
  RemoveAttachmentSuccess,
  RemoveAttachmentFailed,
  GetSupportingInformationHistory,
  FetchAdjustments,
} from './claims-search.actions';
import { ClaimsService } from '../claims-search/claims-search-service';
import { ReconsiderationService } from '../reconsideration/reconsideration.service';
import { ClaimsDetailsService } from '../claims-search/claims-details-service';
import { ClaimsDDARService } from '../claims-search/claims-ddar-service';
import { isDate, format, subMonths } from 'date-fns';
import { ClaimsSearchFacade } from './claims-search.facade';
import {
  ShellTrackHandlers,
  tinSearchTypeLabel,
  fileCountByExtension,
} from '@cigna/chcp/shared/analytics-util';
import {
  ProviderService,
  ProviderByTin,
} from '@cigna/chcp/shared/provider-data-access';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { AttachmentService } from '@cigna/chcp/shared/attachment-data-access';
import {
  Attachment,
  AttachmentMetaData,
  AttachmentToUpload,
  SignedUrlMetaData,
  SupportingInformation,
  ReconsiderationType,
  deepestActivatedRoute,
} from '@cigna/chcp/shared/util';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OmniConversationsFacade } from '@cigna/omni/conversations-state';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FeatureTogglesFacade } from '@cigna/features';
import {
  GraphQLMutations,
  ClaimDetailNewGQLService,
} from '@cigna/chcp/shared/graphql-util';
import { ApolloError } from '@apollo/client/core';

@Injectable()
export class ClaimsSearchEffects {
  claimResults = 'claim/results';
  claimSearch = 'claim/search';
  multiplePatients = 'claim/multiplepatients';
  claimDetails = 'claim/detail';

  search$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.SearchClaims),
      map((action: SearchClaims) => action.payload),
      withLatestFrom(this.claimsSearchFacade.selectedTab$),
      switchMap(([payload, selectedTab]) => {
        const searchClaims: SearchClaim = {
          type: this.getType(payload, selectedTab),
          startDateOfService: isDate(payload.asOfDate)
            ? format(payload.asOfDate as Date, dtFormat)
            : '',
          endDateOfService: isDate(payload.toDate)
            ? format(payload.toDate as Date, dtFormat)
            : '',
          tinNumbers: payload.providerSelAccNum,
          memberId: payload.memberId,
          memberDateOfBirth: isDate(payload.dob)
            ? format(payload.dob as Date, dtFormat)
            : '',
          memberLastName: payload.lastName,
          memberFirstName: payload.firstName,
          providerGeneratedNumber: payload.pvdrGenPatAcctNo
            ? payload.pvdrGenPatAcctNo
            : '',
        };
        return this.claimsService.searchClaims(searchClaims).pipe(
          map((searchResults: ClaimsResultsResponse) => {
            this.pageNavigation(searchResults);
            return new SearchClaimsSuccess(
              searchResults.searchClaimsResponseObj,
            );
          }),
          catchError((err) => of(new SearchClaimsError(err))),
        );
      }),
    ),
  );

  removeAdjustment$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.RemoveAdjustment),
      map(
        (action: { payload: { id: string; reconType: ReconsiderationType } }) =>
          action.payload,
      ),
      switchMap((payload) =>
        this.reconsiderationService
          .removeAdjustment(payload.id, payload.reconType)
          .pipe(map((_) => new RemoveAdjustmentSuccess({ id: payload.id }))),
      ),
      catchError((err) => of(new RemoveAdjustmentError(err))),
    ),
  );

  trackSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.SearchClaims),
        map((action: SearchClaims) => action.payload),
        tap((payload) => {
          if (payload && payload.access) {
            const selectedOption = this.findClaimSearchOption(payload.access);
            this.analytics.trackEventOther(
              {},
              'Search',
              'ac-CS007-Search Parameters',
              {
                prop20: 'Patient Information',
                eVar80: `Patient Information^${selectedOption}`,
                eVar111: `${tinSearchTypeLabel}:claims search`,
                pseudoTin: {
                  type: 'claims search',
                  tins: payload.generatedTinId ? [payload.generatedTinId] : [],
                },
              },
            );
          }
        }),
      ),
    { dispatch: false },
  );

  trackSearchSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ClaimsSearchActionTypes.SearchClaimsSuccess,
          ClaimsSearchActionTypes.ClaimsNumberSearchSuccess,
        ),
        tap(() => {
          this.analytics.trackEventOther(
            {},
            // TODO: resolve CHCP disables by bot
            // eslint-disable-next-line sonarjs/no-duplicate-string
            'API Search Response',
            // eslint-disable-next-line sonarjs/no-duplicate-string
            'ac-CS008-Search Outcome',
            {
              events: 'event162',
            },
          );
        }),
      ),
    { dispatch: false },
  );

  trackClaimSearchFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.SearchClaimsError),
        map((action: SearchClaimsError) => action.payload),
        tap((error) => {
          if (error.status === 404) {
            this.analytics.trackEventOther(
              {},
              'API Search Response',
              'ac-CS008-Search Outcome',
              {
                events: 'event163',
              },
            );
          } else {
            this.analytics.trackServiceError(
              {},
              'claims/v1/search/{type}:GET',
              error,
              'Claim not found. Verify your search criteria and resubmit.',
            );
          }
        }),
      ),
    { dispatch: false },
  );

  trackClaimSearchNumberFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.ClaimsNumberSearchError),
        map((action: ClaimsNumberSearchError) => action.payload),
        tap((error) => {
          if (error.status === 404) {
            this.analytics.trackEventOther(
              {},
              'API Search Response',
              'ac-CS008-Search Outcome',
              {
                events: 'event163',
              },
            );
          } else {
            this.analytics.trackServiceError(
              {},
              'claims/v1/{claimNumber}:GET',
              error,
              'Claim not found. Verify your search criteria and resubmit.',
            );
          }
        }),
      ),
    { dispatch: false },
  );

  searchClaimRefNumber$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.ClaimsNumberSearch),
      map((action: ClaimsNumberSearch) => action.payload),
      withLatestFrom(this.claimsSearchFacade.selectedTab$),
      switchMap(([payload, selectedTab]) => {
        const searchClaimsRef: SearchClaim = {
          type: this.getType(payload, selectedTab),
          claimReferenceNumber: payload.claimReferNo,
          tinNumbers: payload.providerSelRefNum,
        };
        return this.claimsService.searchClaims(searchClaimsRef).pipe(
          map((searchResults: ClaimsResultsResponse) => {
            this.pageNavigation(searchResults);
            return new ClaimsNumberSearchSuccess(
              searchResults.searchClaimsResponseObj,
            );
          }),
          catchError((err) => of(new ClaimsNumberSearchError(err))),
        );
      }),
    ),
  );

  selectHistoryData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.SetHistoryDataIndex),
        map((action: SetHistoryDataIndex) => action.payload),
        withLatestFrom(
          this.claimsSearchFacade.selectedHistoryData$,
          this.claimsSearchFacade.claimRefNum$,
          this.authFacade.isDentalUser$,
        ),
        tap(([, data, claimReferenceNumber, isDentalUser]) => {
          if (data?.status === 'DRAFT') {
            this.router.navigate([data.processStateUrl], {
              queryParams: { claimReferenceNumber },
              queryParamsHandling: 'merge',
            });
          } else {
            this.router.navigate(
              isDentalUser && data?.reviewType === 'Pre-Service'
                ? [
                    `/claim/detail/${claimReferenceNumber}/precertification/pretreatment/${claimReferenceNumber}/confirmation`,
                  ]
                : [
                    `/claim/detail/${claimReferenceNumber}/reconsideration/adjustment-request/confirmation`,
                  ],
              {
                queryParams: { claimReferenceNumber },
                queryParamsHandling: 'merge',
              },
            );
          }
        }),
      ),
    { dispatch: false },
  );

  loadProviderByTin$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.GetProviderByTin),
      map((action: GetProviderByTin) => action.payload),
      withLatestFrom(this.claimsSearchFacade.providers$),
      switchMap(([tin, providerData]) =>
        this.userFacade
          .tinLob$(tin)
          .pipe(map((lob) => ({ tin, lob, providerData }))),
      ),
      switchMap(({ tin, providerData, lob }) => {
        if (!providerData?.length) {
          return this.providerService.getProvidersByTin(tin, lob).pipe(
            map((data: ProviderByTin[]) => new GetProviderByTinSuccess(data)),
            catchError((err) => of(new GetProviderByTinError(err))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  modifySearch$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.ModifySearch),
        map(() => {
          this.router.navigate([this.claimSearch]);
        }),
      ),
    { dispatch: false },
  );

  newSearch$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.NewSearch),
        map(() => {
          this.router.navigate([this.claimSearch]);
        }),
      ),
    { dispatch: false },
  );

  claimDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.DisplayClaimsDetails),
      map((action: DisplayClaimsDetails) => action.payload),
      switchMap((payload) =>
        this.claimDetailGQLService
          .watch(
            {
              input: {
                claimCompositeKey: payload.claimCompositeKey,
                claimReferenceNumber: payload.claimReferenceNumber,
                compositeClaimId: payload.compositeClaimId,
                tinNumbers: payload.tinNumbers,
                medicare: payload.medicare === 'true',
              },
            },
            { fetchPolicy: 'cache-and-network' },
          )
          .valueChanges.pipe(
            map((res) => res.data.claimByCompositeKey),
            tap((data) => {
              const claimDetail: ClaimDetails = data?.detail as ClaimDetails;

              if (claimDetail) {
                this.conversationsFacade.updateConversationMetadata({
                  meta: {
                    hasRemittanceReport:
                      !!data?.detail?.remittanceSummaryData?.remittanceData
                        ?.length,
                    sourceSystemCode:
                      claimDetail.claimInfoObject.sourceSystemCode,
                    dos: claimDetail.claimInfoObject.claimDateOfService,
                    claimStatusDesc:
                      claimDetail.claimInfoObject.claimStatusDesc,
                    claimCompositeKey: payload.claimCompositeKey,
                    claimReferenceNumber: payload.claimReferenceNumber,
                    memberSearch: payload.memberSearch,
                    tin: payload.tinNumbers,
                    memberInfo: {
                      memberDateOfBirth:
                        claimDetail.memberObject.memberDateOfBirth,
                      memberFirstName: claimDetail.memberObject.memberFirstName,
                      memberId: claimDetail.memberObject.memberId,
                      memberLastName: claimDetail.memberObject.memberLastName,
                    },
                  },
                  dataType: 'claimDetails',
                });
              }
            }),
            switchMap((data: { detail: ClaimDetails }) =>
              of(
                new LoadClaimsDDARSuccess({
                  claimDetailsResponse: {
                    ...data.detail,
                    tinNumber: payload.tinNumbers,
                  },
                  remittanceDetailsResponse:
                    data.detail.remittanceSummaryData || null,
                }),
              ),
            ),
            catchError((res: ApolloError) =>
              of(
                new LoadClaimsDetailsError(
                  res.graphQLErrors[0].extensions.metadata.cignaServiceError,
                ),
              ),
            ),
          ),
      ),
    ),
  );

  trackClaimDetailsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.LoadClaimsDetailsError),
        map((action: LoadClaimsDetailsError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'claims/v1/{claimNumber}:GET',
            error,
            'Error loading claim detail',
          );
        }),
      ),
    { dispatch: false },
  );

  multiplePatientsSubmit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.MultiplePatients),
      map((action: MultiplePatients) => ({
        payload: action.payload,
        flaggedData: action.flaggedData,
      })),
      withLatestFrom(
        this.claimsSearchFacade.selectedTab$,
        this.authFacade.isImpersonator$,
      ),
      switchMap(([{ payload, flaggedData }, selectedTab, impersonator]) => {
        const submitMultiplePatients: SearchClaim = {
          type: this.getType(payload, selectedTab),
          startDateOfService: isDate(payload.asOfDate)
            ? format(payload.asOfDate as Date, dtFormat)
            : format(subMonths(new Date(), 6), dtFormat),
          endDateOfService: isDate(payload.toDate)
            ? format(payload.toDate as Date, dtFormat)
            : format(new Date(), dtFormat),
          tinNumbers: payload.providerSelAccNum,
          memberId: payload.memberId,
          memberDateOfBirth: isDate(payload.dob)
            ? format(payload.dob as Date, dtFormat)
            : '',
          memberLastName: payload.lastName,
          memberFirstName: payload.firstName,
          providerGeneratedNumber: payload.pvdrGenPatAcctNo || '',
          claimSystemCode: payload.claimSystemCode || '',
          relationShipCode: payload.relationShipCode,
          memberSearchKey: payload.memberSearchKey,
          isPayerSolution: payload.isPayerSolution,
          hasBehavioral: payload.hasBehavioral,
          isSharedAdmin: payload.isSharedAdmin,
          multiPatient: 'Y',
          patientId: payload.patientId || '',
          memberIdentifier: payload.memberIdentifier || '',
        };
        if (submitMultiplePatients.type === '2') {
          submitMultiplePatients.claimReferenceNumber =
            payload.claimReferenceNumber;
        }
        if (submitMultiplePatients.type === '3') {
          submitMultiplePatients.memberId = payload.patientId;
        }
        return this.claimsService.searchClaims(submitMultiplePatients).pipe(
          map((searchResults: ClaimsResultsResponse) => {
            this.pageNavigation(searchResults);
            if (flaggedData && !impersonator) {
              this.userFacade.updateFlaggedClaims(FlagUpdateType.remove, [
                flaggedData,
              ]);
            }
            return new MultiplePatientsSuccess(
              searchResults.searchClaimsResponseObj,
            );
          }),
          catchError((err) => of(new SearchClaimsError(err))),
        );
      }),
    ),
  );

  backToMultiplePatients$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.BackToMultiplePatients),
        map(() => {
          this.router.navigate([this.multiplePatients]);
        }),
      ),
    { dispatch: false },
  );

  remittanceReadStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.RemittanceReadStatus),
      map((action: RemittanceReadStatus) => action.payload),
      withLatestFrom(this.authFacade.lob$),
      concatMap(([action, lob]) =>
        (action.readStatus
          ? this.claimsDDARService.markAsRead(action, lob || 'MED')
          : this.claimsDDARService.markAsUnread(
              action.remittanceTrackingNumber,
              lob || 'MED',
            )
        ).pipe(
          map(
            (res: RemittanceReadResponse) =>
              new RemittanceReadStatusSuccess({
                ...res,
                remittanceReadData: {
                  ...res.remittanceReadData,
                  remittance: [
                    {
                      Num: action.remittanceTrackingNumber,
                      Cdt: action.creationDate,
                    },
                  ],
                },
              }),
          ),
          catchError((err) => of(new RemittanceReadStatusError(err))),
        ),
      ),
    ),
  );

  fetchSupplementaryClaimInfo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ClaimsSearchActionTypes.FetchSupplementaryClaimInfo,
          ClaimsSearchActionTypes.LoadClaimsDDARSuccess,
          ClaimsSearchActionTypes.LoadClaimsDetailsSuccess,
        ),
        withLatestFrom(this.claimsSearchFacade.claimDetailsClaimInfo$),
        filter(
          ([, claimDetailsInfo]) => !!claimDetailsInfo?.claimReferenceNumber,
        ),
        tap(([, claimDetailsInfo]) => {
          if (claimDetailsInfo) {
            this.claimsSearchFacade.fetchAdjustments(
              claimDetailsInfo.sourceSystemCode === 'DNTC'
                ? claimDetailsInfo.claimEngineClaimID
                : claimDetailsInfo.claimReferenceNumber,
            );
            this.claimsSearchFacade.fetchSupportingInformation(
              claimDetailsInfo.claimReferenceNumber,
              claimDetailsInfo.sourceSystemCode,
              claimDetailsInfo.claimEngineClaimID,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  saveRecentClaim$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ClaimsSearchActionTypes.LoadClaimsDDARSuccess,
          ClaimsSearchActionTypes.LoadClaimsDetailsSuccess,
        ),
        withLatestFrom(
          this.claimsSearchFacade.claimDetails$,
          this.routerFacade.queryParams$,
          this.authFacade.isImpersonator$,
        ),
        filter(
          ([, claimDetails, _queryParams, isImpersonator]) =>
            !isImpersonator &&
            !!claimDetails?.claimInfoObject.claimReferenceNumber,
        ),
        tap(([, claimDetails, queryParams]) => {
          const details = claimDetails as ClaimDetails;

          this.gqlMutationsService.saveRecentClaim({
            tinNumbers: [details.tinNumber || ''],
            member: {
              memberFirstName: details.memberObject.memberFirstName,
              memberLastName: details.memberObject.memberLastName,
              memberId: details.memberObject.memberId,
              memberSearchKey: details.memberObject.memberSearchKey || '',
              claimSystemCode: details.claimInfoObject.claimEngineClaimID,
              memberIdentifier: details.memberObject.memberIdentifier || '',
              relationShipCode: details.memberObject.relationShipCode || '',
            },
            claimSummary: {
              chargeAmount: details.claimInfoObject.claimTotChargeAmount,
              claimCompositeKey: details.claimCompositeKey,
              compositeClaimId: details.claimInfoObject.compositeClaimId,
              claimReferenceNumber:
                details.claimInfoObject.claimReferenceNumber,
              claimStatus: details.claimInfoObject.claimStatusDesc,
              dateOfService: details.claimInfoObject.claimDateOfService,
              medicare: queryParams?.medicare === 'true',
              providerGeneratedAccountNumber:
                details.claimInfoObject.providerGeneratedAccountNumber === '--'
                  ? ''
                  : details.claimInfoObject.providerGeneratedAccountNumber ||
                    '',
            },
          });
        }),
      ),
    { dispatch: false },
  );

  fetchReconHistory$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.FetchAdjustments),
      map((action: FetchAdjustments) => action.payload),
      switchMap((payload) =>
        this.reconsiderationService
          .fetchReconHistory('claimref', payload.claimReferenceNumber)
          .pipe(map((v: Adjustment[]) => new FetchAdjustmentsSuccess(v))),
      ),
      catchError((err) => of(new FetchAdjustmentsError(err))),
    ),
  );

  updateConversationData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.FetchAdjustmentsSuccess),
        tap((action: FetchAdjustmentsSuccess) => {
          const recentData = [...(action.payload || [])].sort((a, b) =>
            (a.updateDate || 0) > (b.updateDate || 0) ? 1 : 0,
          )[0];
          this.conversationsFacade.updateConversationMetadata({
            meta: {
              reconsideration: recentData
                ? {
                    status: recentData.status,
                    submittedDate: recentData.submittedDate,
                    submittedBy: recentData.submittedBy,
                  }
                : undefined,
              reconHistoryLength: action.payload?.length || 0,
            },
            dataType: 'claimDetails',
          });
        }),
      ),
    { dispatch: false },
  );

  getSupportingInformationHistory$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.GetSupportingInformationHistory),
      map((action: GetSupportingInformationHistory) => action.payload),
      switchMap((payload) =>
        this.reconsiderationService
          .getClaimSupportingInformation(
            payload.claimReferenceNumber,
            payload.sourceSystemCode,
            payload.claimEngineClaimID,
          )
          .pipe(
            map(
              (supportingInfos: SupportingInformation[]) =>
                new GetSupportingInformationHistorySuccess(supportingInfos),
            ),
            catchError((err) =>
              of(new GetSupportingInformationHistoryFailed(err)),
            ),
          ),
      ),
    ),
  );

  getAttachmentsSignedUrlToPutFile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.GetAttachmentsSignedUrlToPutFile),
      map((action: GetAttachmentsSignedUrlToPutFile) => action.payload),
      withLatestFrom(this.claimsSearchFacade.supportingInformationSubmssionId$),
      filter(([_, id]) => !!id),
      switchMap(([files, id]) =>
        this.attachmentService
          .getAttachmentsSignedUrlToPutFile(
            id || '',
            files.map((v) => v.file),
            'PENDED',
          )
          .pipe(
            map(
              (signedUrlsMetaData: SignedUrlMetaData[]) =>
                new GetAttachmentsSignedUrlToPutFileSuccess(
                  files.map(
                    (v) =>
                      ({
                        ...v,
                        ...signedUrlsMetaData.find(
                          (meta) => meta.filename === v.file.name,
                        ),
                      } as unknown as AttachmentToUpload),
                  ),
                ),
            ),
            catchError((err) =>
              of(
                new GetAttachmentsSignedUrlToPutFileFailed({
                  error: err,
                  fileNames: files.map((v) => v.file.name),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  putFilesToS3$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.GetAttachmentsSignedUrlToPutFileSuccess),
      map((action: GetAttachmentsSignedUrlToPutFileSuccess) => action.payload),
      switchMap((attachments) =>
        merge(
          ...attachments.map((attachment) =>
            this.attachmentService.putFileToS3(attachment).pipe(
              filter((event: HttpEvent<unknown>) =>
                [HttpEventType.UploadProgress, HttpEventType.Response].includes(
                  event.type,
                ),
              ),
              map((event) =>
                event.type === HttpEventType.UploadProgress
                  ? new FileToS3UploadProgress({
                      ...attachment,
                      loaded: event.loaded,
                    })
                  : new PutFileToS3Success(attachment),
              ),
              catchError((err) =>
                of(
                  new PutFileToS3Failed({
                    error: err,
                    fileNames: [attachment.file.name],
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  getAttachmentsDetailsForUploaded$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.PutFileToS3Success),
      map((action: PutFileToS3Success) => action.payload),
      withLatestFrom(
        this.claimsSearchFacade.claimDetailsClaimInfo$,
        this.claimsSearchFacade.claimDetails$,
      ),
      mergeMap(([attachment, claimInfo, claimDetails]) =>
        this.attachmentService
          .getAttachmentsDetails(
            'PENDED',
            '',
            [attachment.attachmentId],
            '',
            claimInfo?.claimReferenceNumber,
            claimDetails?.tinNumber,
          )
          .pipe(
            map(
              (attachmentsMetaData: AttachmentMetaData[]) =>
                new GetAttachmentDetailsForUploadedSuccess(
                  attachmentsMetaData[0],
                ),
            ),
            catchError((err) =>
              of(
                new GetAttachmentDetailsForUploadedFailed({
                  error: err,
                  fileNames: [attachment.file.name],
                }),
              ),
            ),
          ),
      ),
    ),
  );

  getAttachmentsDetailsForSubmitted$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.GetSupportingInformationAttachments),
      map((action: GetSupportingInformationAttachments) => action.payload),
      mergeMap(({ id, attachmentIds }) =>
        this.attachmentService
          .getAttachmentsDetails('PENDED', id, attachmentIds)
          .pipe(
            map(
              (attachments: AttachmentMetaData[]) =>
                new GetSupportingInformationAttachmentsSuccess({
                  id,
                  attachments,
                }),
            ),
            catchError((err) =>
              of(new GetSupportingInformationAttachmentsFailed(err)),
            ),
          ),
      ),
    ),
  );

  attachmentClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.AttachmentClick),
        map((action: AttachmentClick) => action.payload),
        // tap(() => { // TODO: find out right analytic action
        //   this.analytics.trackEventOther({}, 'Attachment clicked', 'ac-rec020');
        // }),
        switchMap((attachment) =>
          this.attachmentService.getAttachmentsDetails('PENDED', '', [
            attachment.id,
          ]),
        ),
        tap((attachmentsMeta: AttachmentMetaData[]) => {
          if (attachmentsMeta?.length && attachmentsMeta[0]?.signedUrl) {
            this._window.open(attachmentsMeta[0].signedUrl, '_blank');
          }
        }),
      ),
    { dispatch: false },
  );

  deleteAttachment$: Observable<Action> = this.actions$.pipe(
    ofType(ClaimsSearchActionTypes.RemoveAttachment),
    map((action: RemoveAttachment) => action.payload),
    withLatestFrom(this.claimsSearchFacade.supportingInformationUploaded$),
    map(([fileName, uploadedAttachments]) =>
      uploadedAttachments.find((attachment) => attachment.name === fileName),
    ),
    filter((attachment) => !!attachment?.id),
    switchMap((attachment) =>
      this.attachmentService.deleteAttachment(attachment?.id || '').pipe(
        map(() => new RemoveAttachmentSuccess(attachment?.name || '')),
        catchError((err) => of(new RemoveAttachmentFailed(err))),
      ),
    ),
  );

  submitSupportingInformation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ClaimsSearchActionTypes.SubmitSupportingInformation),
      withLatestFrom(
        this.claimsSearchFacade.claimDetailsClaimInfo$,
        this.claimsSearchFacade.supportingInformationUploaded$,
        this.claimsSearchFacade.supportingInformationSubmssionId$,
      ),
      filter(
        ([_, claimInfo, attachments, id]) =>
          !!(claimInfo && attachments.length && id),
      ),
      map(([_, claimInfo, attachments, id]) => [claimInfo, attachments, id]),
      switchMap(
        ([claim, attachments, id]: [ClaimsInfo, Attachment[], string]) =>
          this.reconsiderationService
            .submitClaimSupportingInformation({
              id,
              claimReferenceNumber: claim.claimReferenceNumber,
              sourceSystemCode: claim.sourceSystemCode,
              claimEngineClaimID: claim.claimEngineClaimID,
              attachments: attachments.map((v) => v.id),
            })
            .pipe(
              map((supInfo) => new SubmitSupportingInformationSuccess(supInfo)),
              catchError((err) =>
                of(new SubmitSupportingInformationFailed(err)),
              ),
            ),
      ),
    ),
  );

  trackSupportingInformation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.SubmitSupportingInformation),
        withLatestFrom(this.claimsSearchFacade.supportingInformationUploaded$),
        tap(([, attachments]) => {
          this.analytics.trackEventOther({}, 'Submit', 'ac-CS020', {
            prop20: 'Supporting Information modal',
            eVar111: `type:pended claim^attach:${
              attachments.length
            }${fileCountByExtension(attachments, '.bmp')}${fileCountByExtension(
              attachments,
              '.gif',
            )}${fileCountByExtension(
              attachments,
              '.jpeg',
            )}${fileCountByExtension(
              attachments,
              '.jpg',
            )}${fileCountByExtension(
              attachments,
              '.pdf',
            )}${fileCountByExtension(
              attachments,
              '.png',
            )}${fileCountByExtension(
              attachments,
              '.tif',
            )}${fileCountByExtension(attachments, '.tiff')}`,
          });
        }),
      ),
    { dispatch: false },
  );

  routeToSupInfoSubmissionDetails$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClaimsSearchActionTypes.SubmitSupportingInformationSuccess),
        map((action: SubmitSupportingInformationSuccess) => action.payload),
        map((supInfo) => {
          this.matDialog.closeAll();
          this.router.navigate(['supporting-info', supInfo.id], {
            queryParamsHandling: 'preserve',
            relativeTo: deepestActivatedRoute(this.router),
          });
          this.analyticsService.track('voc-claims-attach');
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private router: Router,
    private claimsService: ClaimsService,
    private claimsDetailsService: ClaimsDetailsService,
    private claimsDDARService: ClaimsDDARService,
    private claimsSearchFacade: ClaimsSearchFacade,
    private authFacade: AuthFacade,
    private analytics: ShellTrackHandlers,
    private providerService: ProviderService,
    private reconsiderationService: ReconsiderationService,
    private attachmentService: AttachmentService,
    private _window: WindowService,
    private matDialog: MatDialog,
    private routerFacade: RouterFacade,
    private analyticsService: AnalyticsService,
    private userFacade: UserFacade,
    public conversationsFacade: OmniConversationsFacade,
    private featureTogglesFacade: FeatureTogglesFacade,
    private gqlMutationsService: GraphQLMutations,
    private claimDetailGQLService: ClaimDetailNewGQLService,
  ) {}

  private getType(payload: any, type: any) {
    let returnValue = '';
    if (
      payload.access !== 'patient_account_number' &&
      type === ClaimTab.MEMBER
    ) {
      returnValue = '1';
    } else if (type === ClaimTab.CLAIM_NUMBER) {
      returnValue = '2';
    } else if (payload.access === 'patient_account_number') {
      returnValue = '3';
    }
    return returnValue;
  }

  private pageNavigation(results: ClaimsResultsResponse) {
    if (results.searchClaimsResponseObj?.memberList?.length > 1) {
      this.router.navigate([this.multiplePatients]);
    } else {
      this.router.navigate([this.claimResults]);
    }
  }
  private findClaimSearchOption(access: string) {
    let seachOption = '';
    switch (access) {
      case 'id_dob':
        seachOption = 'Patient ID/Date of Birth';
        break;
      case 'id_name':
        seachOption = 'Patient ID/Name';
        break;
      case 'dob_name':
        seachOption = 'Date of Birth/Name';
        break;
      case 'id_dob_name':
        seachOption = 'Patient ID/Date of Birth/Name';
        break;
      case 'patient_account_number':
        seachOption = 'Provider Generated Patient Account Number';
        break;
      default:
        seachOption = '';
        break;
    }
    return seachOption;
  }
}
